<template>
  <div class="xl:w-full xl:h-full flex xl:flex-nowrap flex-wrap">
    <div
      class="xl:w-2/3 sm:items-start items-center sm:text-left text-center w-full xl:h-full xl:pt-14 pt-6 xl:px-14 lg:px-20 sm:px-14 px-5 text-gray-700 text-base"
    >
      <h1 class="mt-10" id="Merchant_API">Merchant API</h1>
      <h2 class="mt-10" id="Introduction">Introduction</h2>
      <p>
        This document describes how you can make a request to our endpoint to
        send us receipt data.
      </p>
      <p>
        Make a POST request to our endpoint and wait for a positive 200
        response.
      </p>
      <h3 id="Endpoint_URL">Endpoint URL</h3>
      <ul class="list-disc text-left overflow-auto pl-10 mb-4">
        <li>
          Production: <code>https://input.api.getbanyan.com/receipt</code>
        </li>
        <li>
          Development:
          <code>https://input.api.development.getbanyan.com/receipt</code>
        </li>
      </ul>
      <h2 class="mt-10" id="Example_Payload">Example Payload</h2>
      <v-card class="p-4 block text-left" dark>
        <v-tabs v-model="tab0" dark>
          <v-tab v-for="item in chart0" :key="item.tab">
            {{ item.tab }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab0" dark>
          <v-tab-item v-for="item in chart0" :key="item.tab">
            <v-card flat>
              <div v-if="item.tab == 'Example'">
                <v-card-text
                  ><div>
                    <pre>{{ item.content }}</pre>
                  </div></v-card-text
                >
              </div>
              <div v-else>
                <v-data-table :headers="headers" :items="item.content">
                </v-data-table>
              </div>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-card>

      <h2 class="mt-10" id="Example_Response">Example Response</h2>
      <v-card class="p-4 block text-left" dark>
        <v-tabs v-model="tab1" dark>
          <v-tab v-for="item in chart1" :key="item.tab">
            {{ item.tab }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab1" dark>
          <v-tab-item v-for="item in chart1" :key="item.tab">
            <v-card flat>
              <div v-if="item.tab == 'Example'">
                <v-card-text
                  ><div>
                    <pre>{{ item.content }}</pre>
                  </div></v-card-text
                >
              </div>
              <div v-else>
                <v-data-table :headers="headers" :items="item.content">
                </v-data-table>
              </div>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-card>

      <h2 class="mt-10" id="Error_Handling">Error Handling</h2>
      <p>
        The receipt will be validated upon delivery, and if there is an error,
        we will report it in our request response
      </p>
      <v-card class="p-4 block text-left" dark>
        <v-tabs v-model="tab2" dark>
          <v-tab v-for="item in chart2" :key="item.tab">
            {{ item.tab }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab2" dark>
          <v-tab-item v-for="item in chart2" :key="item.tab">
            <v-card flat>
              <div v-if="item.tab == 'Example'">
                <v-card-text
                  ><div>
                    <pre>{{ item.content }}</pre>
                  </div></v-card-text
                >
              </div>
              <div v-else>
                <v-data-table :headers="headers" :items="item.content">
                </v-data-table>
              </div>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </div>

    <div
      class="xl:w-1/3 xl:block hidden w-full xl:self-stretch pt-20 px-6 relative"
    >
      <div class="overflow-y-auto fixed h-full mb-30" :class="[isOffset ? 'top-4': 'top-20']">
        <p class="ml-10">
          <vue-fontawesome
            icon="clock-o"
            class="pr-2"
            size="1"
            color="#FCD34D"
          ></vue-fontawesome>
          <span class="text-xs">Updated on january 27, 2017</span>
        </p>
        <ul class="ml-8">
          <li class="text-base py-2 hover:text-blue-500 hover:bg-gray-50 px-2">
            <span @click="scrollToElement('#Merchant_API')">Merchant API</span>
          </li>
          <li class="text-base py-2 hover:text-blue-500 hover:bg-gray-50 px-2">
            <span @click="scrollToElement('#Introduction')">Introduction</span>
          </li>
          <li class="text-base py-2 hover:text-blue-500 hover:bg-gray-50 px-2">
            <span @click="scrollToElement('#Endpoint_URL')">Endpoint URL</span>
          </li>
          <li class="text-base py-2 hover:text-blue-500 hover:bg-gray-50 px-2">
            <span @click="scrollToElement('#Example_Payload')">Example Payloads</span>
          </li>
          <li class="text-base py-2 hover:text-blue-500 hover:bg-gray-50 px-2">
            <span @click="scrollToElement('#Example_Response')">Example Response</span>
          </li>
          <li class="text-base py-2 hover:text-blue-500 hover:bg-gray-50 px-2">
            <span @click="scrollToElement('#error-handling')">Error Handling</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "Merchant_API",
  computed: {
    ...mapGetters("common", ["isOffset"]),
  },
  data: () => ({
    headers: [
      {
        text: "Name",
        align: "start",
        sortable: false,
        value: "name",
      },
      { text: "Type", value: "type" },
    ],
    // DATA START
    tab0: null,
    chart0: [
      {
        tab: "Example",
        content: `{
    "store": {
        "displayName": "Greensburg Supercenter",
        "phoneNumber": "812-663-3434",
        "address": {
            "country": "us",
            "state": "ca",
            "city": "greensburg",
            "postalCode": "47240",
            "addressLineOne": "790 GREENSBURG COMMONS CTR",
            "addressLineTwo": "GREENSBURG IN 47240"
        }
    },
    "transaction": {
        "datetime": "2020-12-30T23:59:59.000Z",
        "cardType": "visa",
        "last4": "1234",
        "totalAmount": 12.24,
        "subtotal": 11.44,
        "taxAmount": 0.8,
        "changeDue": 0,
        "tipAmount": 0,
        "items": [
            {
                "itemId": "0576407142",
                "upc": "068113124766",
                "description": "Equate Moisturizing Hand Sanitizer with Vitamin E, 34 fl oz - May Ship with a Flip Top Cap",
                "price": 3.97,
                "quantity": 1
            },
            {
                "itemId": "0565760620",
                "upc": "003920822288",
                "description": "Hyper Tough Indoor Analog Timer, Double Grounded Outlet",
                "price": 7.47,
                "quantity": 1
            }
        ]
    }
}`,
      },
      {
        tab: "Structure",
        content: [
          { name: "displayName", type: "string" },
          { name: "phoneNumber", type: "string" },
          { name: "country", type: "string" },
          { name: "state", type: "string" },
          { name: "city", type: "string" },
          { name: "postalCode", type: "string" },
          { name: "addressLineOne", type: "string" },
          { name: "addressLineTwo", type: "string" },
          { name: "datetime", type: "string" },
          { name: "cardType", type: "string" },
          { name: "last4", type: "string" },
          { name: "totalAmount", type: "number" },
          { name: "subtotal", type: "number" },
          { name: "taxAmount", type: "number" },
          { name: "changeDue", type: "number" },
          { name: "tipAmount", type: "number" },
          { name: "itemId", type: "string" },
          { name: "upc", type: "string" },
          { name: "description", type: "string" },
          { name: "price", type: "number" },
          { name: "quantity", type: "number" },
          { name: "itemId", type: "string" },
          { name: "upc", type: "string" },
          { name: "description", type: "string" },
          { name: "price", type: "number" },
          { name: "quantity", type: "number" },
        ],
      },
    ],

    tab1: null,
    chart1: [
      {
        tab: "Example",
        content: `{
    "message": "OK"
}`,
      },
      { tab: "Structure", content: [{ name: "message", type: "string" }] },
    ],

    tab2: null,
    chart2: [
      {
        tab: "Example",
        content: `{
    "error": {
        "message": "Error: Payload format incorrect.",
        "code": 404
    }
}`,
      },
      {
        tab: "Structure",
        content: [
          { name: "message", type: "string" },
          { name: "code", type: "number" },
        ],
      },
    ],
    // DATA END
  }),
  methods: {
    scrollToElement(el) {
      document.querySelector(el).scrollIntoView({
        behavior: 'smooth'
      });
    }
  }
};
</script>
<style scoped>
.v-tabs-items {
  overflow-x: auto;
}
</style>